import React, { Dispatch } from 'react';
import { Divider, Stack, Tooltip, Typography } from '@mui/material';
import { styles } from './styles';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LinkedIn as LinkedInIcon,
  Man4 as Man4Icon,
  Event as EventIcon,
  LocationOn as LocationOnIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import moment from 'moment-timezone';
import { formatAddress } from '../Candidate/ProfileTab';
import { useQueryClient } from '@tanstack/react-query';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { ExternalViewAction } from './reducer';

const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export default function ExternalViewHeader({
  dispatch
}: {
  dispatch: Dispatch<ExternalViewAction>;
}) {
  const queryClient = useQueryClient();
  const externalViewData = queryClient.getQueryData<any>(['externalViewData']);
  const candidateFirstName = externalViewData?.application.candidate.firstname;
  const candidateLastName = externalViewData?.application.candidate.lastname;
  const candidateLinkedInURL = externalViewData?.application.candidate.linkedin_url;
  const getLinkedInProfileName = () => {
    const profileName = candidateLinkedInURL?.split('/')[4];
    return !candidateLinkedInURL || !profileName ? 'Search Candidate' : profileName;
  };
  return (
    <Stack
      sx={{
        flexDirection: { xs: 'column', md: 'row' },
        columnGap: 2,
        rowGap: { xs: 2, md: 0 }
      }}
    >
      <Stack sx={{ minWidth: { xs: '100%', md: '15%' } }}>
        <Stack sx={{ color: '#666666', fontSize: '12px', fontWeight: 600 }}>
          Application #{externalViewData?.application.id}
        </Stack>
        <Stack
          sx={{
            color: '#084D6D',
            fontSize: { xs: '18px', sm: '20px' },
            fontWeight: 'bold'
          }}
        >
          {externalViewData?.application.candidate.firstname}{' '}
          {externalViewData?.application.candidate.lastname}
        </Stack>
        <Stack
          sx={{
            color: '#939393',
            fontSize: { xs: '14px', sm: '16px' },
            wordBreak: 'break-word'
          }}
        >
          {externalViewData?.application.job.title}
        </Stack>
      </Stack>
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          margin: { xs: '8px 0', md: '0px 16px' },
          display: { xs: 'none', md: 'block' }
        }}
      />
      {/* Mobile horizontal divider */}
      <Divider
        orientation="horizontal"
        flexItem
        sx={{
          margin: { xs: '8px 0', md: '0px' },
          display: { xs: 'block', md: 'none' }
        }}
      />
      <Stack sx={{ rowGap: 2, paddingTop: 0.5 }}>
        <Stack sx={styles.externalViewHeaderItems}>
          <EmailIcon sx={styles.scoutNavyIcon} />
          <Stack
            sx={{
              fontSize: { xs: '13px', sm: '14px' },
              wordBreak: 'break-all'
            }}
          >
            {externalViewData?.application.candidate.email}
          </Stack>
          <CopyToClipboard
            text={externalViewData?.application.candidate.email}
            onCopy={() => {
              dispatch({
                type: 'SET_SNACKBAR',
                payload: { message: 'Email copied to clipboard!', state: 'success' }
              });
            }}
          >
            <Tooltip placement="top" title="Copy email">
              <CopyIcon
                sx={{
                  cursor: 'pointer',
                  color: '#666666',
                  fontSize: '16px'
                }}
              />
            </Tooltip>
          </CopyToClipboard>
        </Stack>
        <Stack sx={styles.externalViewHeaderItems}>
          <PhoneIcon sx={styles.scoutNavyIcon} />
          <Stack sx={{ fontSize: { xs: '13px', sm: '14px' } }}>
            {externalViewData?.application.candidate.phone1}
          </Stack>
        </Stack>
      </Stack>
      <Stack sx={{ rowGap: 2, paddingTop: 0.5, minWidth: { xs: '100%', md: '150px' } }}>
        <Stack sx={styles.externalViewHeaderItems}>
          <LinkedInIcon sx={styles.scoutNavyIcon} />
          <Typography
            sx={styles.linkedInURLLink}
            onClick={() =>
              window.open(
                candidateLinkedInURL ||
                  `https://www.linkedin.com/pub/dir?firstName=${candidateFirstName}&lastName=${candidateLastName}&trk=people-guest_people-search-bar_search-submit`,
                '_blank'
              )
            }
          >
            {getLinkedInProfileName()}
          </Typography>
        </Stack>
        {externalViewData?.application.candidate.gender_full && (
          <Stack sx={styles.externalViewHeaderItems}>
            <Man4Icon sx={styles.scoutNavyIcon} />
            <Stack sx={{ fontSize: { xs: '13px', sm: '14px' } }}>
              {externalViewData?.application.candidate.gender_full}
            </Stack>
          </Stack>
        )}
      </Stack>
      <Stack sx={{ rowGap: 2, paddingTop: 0.5, maxWidth: { sx: '100%', md: '25%' } }}>
        {externalViewData?.application.created_at && (
          <Stack sx={styles.externalViewHeaderItems}>
            <EventIcon sx={styles.scoutNavyIcon} />
            <Stack
              sx={{
                fontSize: { xs: '13px', sm: '14px' },
                wordBreak: 'break-word'
              }}
            >
              {moment
                .tz(
                  externalViewData?.application.created_at,
                  externalViewData?.application.job.time_zone || browserTimezone
                )
                .format('ddd, D MMM YYYY, hh:mma z')}
            </Stack>
          </Stack>
        )}
        <Stack sx={styles.externalViewHeaderItems}>
          <LocationOnIcon sx={styles.scoutNavyIcon} />
          <Stack
            sx={{
              fontSize: { xs: '13px', sm: '14px' },
              wordBreak: 'break-word'
            }}
          >
            {formatAddress(externalViewData?.application.candidate?.address_details)}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
